import styles from './SingleAssetSelfTest.module.scss';
import AnalyticsTable from '@customer/components/tables/analytics-table/AnalyticsTable';
import { GridControlled } from '@customer/components/tables/grid/GridControlled';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo } from 'react';
import { ColDef } from 'ag-grid-community';
import {
  AssetFaultModel,
  AssetSelfTestQueryParams,
  SystemTypePpu,
  useGetSingleAssetsQuery,
  useListAssetFaultsQuery,
} from '@customer/store/apis/assetsApi';
import { useNavigate, useParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query';
import SingleAssetSubPage from '@customer/templates/single-asset-sub-page/SingleAssetSubPage';
import { useAppSelector } from '@customer/store/hooks';
import { FaultLevel } from '@customer/store/slices/globalSlice';
import GlobalTimeRangeFilter from '@customer/components/inputs/global-time-range/GlobalTimeRangeFilter';
import SingleAssetSelfTestOverview from './components/overview/SingleAssetSelfTestOverview';
import ChartScenarios from '@customer/components/charts/chart-scenarios/ChartScenarios';
import {
  ChartColorScheme,
  CustomColorSchemeFunc,
  ScenarioConfig,
  schemeRedOrangeYellow,
  ThemeColorPair,
} from '@customer/components/charts/config';
import { ScenarioDataSet, StatsScenario } from '@customer/store/apis/scenariosApi';
import BarChartByFaultCategory from '@customer/components/charts/by-fault-category/BarChartByFaultCategory';
import BarChartByFaultTestName from '@customer/components/charts/by-fault-test-name/BarChartByFaultTestName';
import FilterButton from '@customer/components/inputs/filter-button/FilterButton';
import FilterSummary from '@customer/components/inputs/filter-summary/FilterSummary';
import {
  columnDefAssetActiveFault,
  columnDefAssetFaultCategory,
  columnDefAssetFaultLevel,
  columnDefAssetFaultName,
  colDefDuration,
} from '@customer/components/tables/grid/columnDefs';
import { FilterGroup } from '@customer/filters/types';
import useFilters from '@customer/filters/useFilters';
import { useAbsoluteTimePartialColDef } from '@customer/components/tables/cells/absolute-time/useAbsoluteTime';

const AssetFaultsGridSliceKey = 'AssetFaults';

const getFilteredFaultLevelColorScheme: CustomColorSchemeFunc = data => {
  let colorScheme: ThemeColorPair[] = [];

  if (typeof data !== 'undefined') {
    // Add the colors that match the returned fault level datasets.
    for (let i = 0, faultLevels = Object.values(FaultLevel); i < faultLevels.length; i++) {
      const faultLevel = faultLevels[i];
      const hasMatchingDataSet = data.dataSets.some(
        (dataSet: ScenarioDataSet) => dataSet.label === faultLevel,
      );
      if (hasMatchingDataSet) {
        colorScheme.push(schemeRedOrangeYellow[i % schemeRedOrangeYellow.length]);
      }
    }
  } else {
    // Default to the full color scheme if there is no available data.
    colorScheme = [...schemeRedOrangeYellow];
  }

  return colorScheme;
};

const getRowId = (data: AssetFaultModel) => data.ts + '__' + data.flt;

export function SingleAssetSelfTest() {
  const { assetId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const userData = useAppSelector(state => state.auth);
  const timeRange = useAppSelector(state => state.global.timeRange);

  const filters = useFilters<AssetSelfTestQueryParams>(FilterGroup.AssetSelfTest);

  const { currentData: asset } = useGetSingleAssetsQuery(assetId ?? skipToken, {
    refetchOnMountOrArgChange: 10,
  });

  const absoluteTimeColDef = useAbsoluteTimePartialColDef<AssetFaultModel>();

  useEffect(() => {
    if (asset && asset.hw === SystemTypePpu) {
      // PPUs don't have configuration or diagnostics, so switch to the appropriate tab
      navigate(`/asset/${asset.id}/details`);
    }
  }, [asset, navigate]);

  const columnDefs = useMemo<ColDef<AssetFaultModel>[]>(
    () => [
      { field: 'ts', headerName: t('Date and Time'), sort: 'desc', ...absoluteTimeColDef },
      { ...columnDefAssetFaultLevel(t), maxWidth: 100 },
      { ...columnDefAssetFaultCategory(t) },
      { ...columnDefAssetFaultName(t) },
      { ...colDefDuration(t('Duration')) },
      { ...columnDefAssetActiveFault(t) },
    ],
    [t, absoluteTimeColDef],
  );

  const isSkipQuery = typeof assetId === 'undefined' || typeof userData.siteId === 'undefined';

  const { data: faultsData, isFetching } = useListAssetFaultsQuery(
    isSkipQuery
      ? skipToken
      : {
          assetId,
          query: {
            ...filters,
            timeRange,
          },
        },
    { refetchOnMountOrArgChange: 10 },
  );

  const getDataSetLabel = useCallback(
    (label: string) => {
      return t('LevelFaults', { level: Number(label) });
    },
    [t],
  );

  const scenarioConfigs = useMemo<ScenarioConfig[]>(
    () => [
      {
        title: t('ByCategory'),
        ChartComponent: BarChartByFaultCategory<AssetSelfTestQueryParams>,
        scenario: StatsScenario.AssetFaultsByCategory,
      },
      {
        title: t('ByTestName'),
        ChartComponent: BarChartByFaultTestName<AssetSelfTestQueryParams>,
        scenario: StatsScenario.AssetFaultsByTestType,
      },
    ],
    [t],
  );

  return (
    <SingleAssetSubPage
      headerActions={
        <>
          <div className={styles.headerText}>
            <FilterSummary
              transKey="ShowingFaults"
              filteredTransKey="ShowingFilteredFaults"
              countAll={faultsData?.countAll}
              countFilter={faultsData?.countFilter}
              filterGroup={FilterGroup.AssetSelfTest}
            />
          </div>
          <div className={styles.headerActions}>
            <GlobalTimeRangeFilter disabled={isFetching} />
            <FilterButton filters={FilterGroup.AssetSelfTest} disabled={isFetching} />
          </div>
        </>
      }
    >
      <>
        <SingleAssetSelfTestOverview assetId={assetId} />

        <ChartScenarios
          title={t('Faults')}
          scenarioConfigs={scenarioConfigs}
          filterGroup={FilterGroup.AssetSelfTest}
          colorScheme={ChartColorScheme.Custom}
          customColorSchemeFunc={getFilteredFaultLevelColorScheme}
          getDataSetLabel={getDataSetLabel}
          tooltipTransKey="FaultsWithCount"
          includeAssetId
          assetId={assetId}
          includeTimeRange
        />

        <AnalyticsTable
          title={t('Faults')}
          hasData={typeof faultsData !== 'undefined'}
          noDataDescription={t('No faults')}
        >
          <GridControlled<AssetFaultModel>
            sliceKey={AssetFaultsGridSliceKey}
            rowData={faultsData?.items ?? []}
            columnDefs={columnDefs}
            getRowId={getRowId}
            noRowsMessage={t('No faults')}
          />
        </AnalyticsTable>
      </>
    </SingleAssetSubPage>
  );
}

export default SingleAssetSelfTest;
